import React from "react";
import styled from "styled-components";
import {
  brand_orange_deep,
  narrowScreenMediaQuery,
} from "../../constants/styles";
import LessonInlineActions from "./LessonInlineActions";
import LessonProgressChip from "./LessonProgressChip";

// Title row + engagement bar that sits BELOW the player. The reference
// design's curriculum-hero band is rendered separately via
// CurriculumContextBand below the player so the video stays above the fold.
function LessonHero({
  lesson,
  courseSlug,
  curriculumSlug,
  courseName,
  curriculumName,
  detailPath,
}) {
  if (!lesson) return null;
  // `detailPath` comes from the server, which is the only place that knows the
  // right shape: a standalone lesson lives at /video-pembelajaran/:slug and has no
  // course or curriculum slug, so building the three-segment path here would share
  // "/sertifikasi/undefined/undefined/...".
  const detailLink =
    detailPath || `/sertifikasi/${courseSlug}/${curriculumSlug}/${lesson.slug}`;
  const contextLine = [courseName, curriculumName].filter(Boolean).join(" › ");
  return (
    <Section>
      <Heading>
        {contextLine && <Context>{contextLine}</Context>}
        {lesson.category_name && <Category>{lesson.category_name}</Category>}
        <Title>{lesson.name}</Title>
        {typeof lesson.lesson_progress_percent === "number" && (
          <ProgressInline>
            <LessonProgressChip
              percent={lesson.lesson_progress_percent}
              durationSeconds={lesson.video_recording?.duration_seconds || 0}
              variant="chip"
            />
          </ProgressInline>
        )}
      </Heading>
      <ActionsRow>
        <LessonInlineActions
          resourceId={lesson.id}
          initialLiked={!!lesson.is_liked}
          initialDisliked={!!lesson.is_disliked}
          initialBookmarked={!!lesson.is_bookmarked}
        />
        <ShareButton
          type="button"
          onClick={async () => {
            try {
              if (navigator.share) {
                await navigator.share({ title: lesson.name, url: detailLink });
                return;
              }
              if (navigator.clipboard) {
                await navigator.clipboard.writeText(
                  window.location.origin + detailLink,
                );
                // Lightweight feedback. Alert is acceptable here — same as
                // the legacy share button in the lessons show page.
                alert("Tautan disalin!");
              }
            } catch (_) {
              /* user cancelled */
            }
          }}
          aria-label="Bagikan materi"
        >
          <ShareIcon aria-hidden="true" viewBox="0 0 24 24">
            <path
              d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
            <path
              d="M16 6l-4-4-4 4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
            <path
              d="M12 2v14"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
            />
          </ShareIcon>
          <span>Bagikan</span>
        </ShareButton>
      </ActionsRow>
    </Section>
  );
}

export default React.memo(LessonHero);

const Section = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  flex-wrap: wrap;
  padding: 4px 0;
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  flex: 1;
`;

const ProgressInline = styled.div`
  margin-top: 6px;
`;

const Context = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const Category = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${brand_orange_deep};
`;

const Title = styled.h1`
  margin: 0;
  font-size: 32px;
  font-weight: 800;
  color: #1f2937;
  line-height: 1.22;
  letter-spacing: -0.005em;

  @media ${narrowScreenMediaQuery} {
    font-size: 24px;
  }
  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const ShareButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 999px;
  background: transparent;
  color: ${brand_orange_deep};
  border: 2px solid ${brand_orange_deep};
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    filter 150ms ease,
    background 150ms ease;

  &:hover {
    background: rgba(216, 89, 7, 0.08);
  }
  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }
`;

const ShareIcon = styled.svg`
  width: 16px;
  height: 16px;
`;
